import { useNavigate } from "react-router-dom";

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div style={pageWrapper}>
      {/* Header */}
      <header style={header}>
        <div style={headerContent}>
          <h1 style={logo} onClick={() => navigate("/")}>Adsoar</h1>
          <button style={backButton} onClick={() => navigate("/")}>
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div style={contentWrapper}>
        <div style={titleSection}>
          <h1 style={mainTitle}>Terms of Service</h1>
        </div>

        <div style={content}>
          <p style={intro}>
            Welcome to Adsoar. By accessing or using <a href="https://adsoar.cc" style={link}>https://adsoar.cc</a> ("Adsoar", "we", "our", or "us"), you agree to these Terms of Service ("Terms"). If you do not agree, do not use the service.
          </p>

          <section style={section}>
            <h2 style={sectionTitle}>1. Description of Service</h2>
            <p style={paragraph}>
              Adsoar is a software platform that allows users to connect their Meta (Facebook) advertising accounts to view, analyze, and manage advertising campaigns and performance data.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>2. Eligibility</h2>
            <p style={paragraph}>
              You must be at least 18 years old and legally authorized to manage any advertising accounts you connect to Adsoar.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>3. Account Registration and Use</h2>
            <p style={paragraph}>You are responsible for:</p>
            <ul style={list}>
              <li style={listItem}>Maintaining the security of your account credentials</li>
              <li style={listItem}>All activity that occurs under your account</li>
              <li style={listItem}>Ensuring that any data or accounts you connect are owned by you or you have permission to use them</li>
            </ul>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>4. Meta Platform Integration</h2>
            <p style={paragraph}>
              Adsoar accesses Meta advertising data and performs actions only with your explicit permission.
            </p>
            <p style={paragraph}>
              Use of Meta services is subject to Meta's Platform Terms, Developer Policies, and applicable Meta rules. Adsoar is not affiliated with Meta.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>5. Acceptable Use</h2>
            <p style={paragraph}>You agree not to:</p>
            <ul style={list}>
              <li style={listItem}>Use Adsoar in violation of any laws or Meta policies</li>
              <li style={listItem}>Attempt to reverse engineer, disrupt, or misuse the platform</li>
              <li style={listItem}>Use Adsoar for fraudulent, misleading, or abusive advertising activity</li>
            </ul>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>6. Service Availability</h2>
            <p style={paragraph}>
              Adsoar is provided on an "as is" and "as available" basis. We do not guarantee uninterrupted access, error-free operation, or specific advertising or financial results.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>7. Fees and Billing</h2>
            <p style={paragraph}>
              Certain features of Adsoar may require payment. Pricing is based on connected monthly advertising spend and selected plans. Fees, billing terms, and renewal details will be presented at the time of purchase.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>8. Termination</h2>
            <p style={paragraph}>You may stop using Adsoar at any time.</p>
            <p style={paragraph}>
              We reserve the right to suspend or terminate access to the service if these Terms are violated or if required to comply with law or platform policies.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>9. Data and Privacy</h2>
            <p style={paragraph}>
              Use of Adsoar is subject to our <a href="/privacy" style={link}>Privacy Policy</a>, which describes how we collect, use, and protect data.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>10. Limitation of Liability</h2>
            <p style={paragraph}>
              To the maximum extent permitted by law, Adsoar shall not be liable for indirect, incidental, or consequential damages arising from use of the service.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>11. Changes to These Terms</h2>
            <p style={paragraph}>
              We may update these Terms from time to time. Continued use of Adsoar after changes become effective constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section style={section}>
            <h2 style={sectionTitle}>12. Contact Information</h2>
            <p style={paragraph}>For questions about these Terms, contact:</p>
            <p style={contactInfo}>
              <strong>Email:</strong> <a href="mailto:matthew@adsoar.cc" style={link}>matthew@adsoar.cc</a>
            </p>
            <p style={contactInfo}>
              <strong>Website:</strong> <a href="https://adsoar.cc" style={link}>https://adsoar.cc</a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer style={footer}>
        <p>&copy; {new Date().getFullYear()} Adsoar LLC. All rights reserved.</p>
      </footer>
    </div>
  );
};

/* Styles */
const pageWrapper = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  background: "#f8fafc",
};

const header = {
  background: "white",
  borderBottom: "1px solid #e2e8f0",
  padding: "20px 0",
  position: "sticky",
  top: 0,
  zIndex: 100,
};

const headerContent = {
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "0 24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const logo = {
  fontSize: "1.5rem",
  fontWeight: 800,
  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  cursor: "pointer",
  margin: 0,
};

const backButton = {
  background: "transparent",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  padding: "10px 20px",
  borderRadius: "8px",
  fontSize: "0.95rem",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.2s",
};

const contentWrapper = {
  flex: 1,
  maxWidth: "900px",
  margin: "0 auto",
  padding: "60px 24px",
  width: "100%",
};

const titleSection = {
  marginBottom: "48px",
  textAlign: "center",
};

const mainTitle = {
  fontSize: "3rem",
  fontWeight: 800,
  color: "#0f172a",
  marginBottom: "16px",
  letterSpacing: "-0.02em",
};

const content = {
  background: "white",
  borderRadius: "16px",
  padding: "48px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  border: "1px solid #e2e8f0",
};

const intro = {
  fontSize: "1.1rem",
  lineHeight: "1.8",
  color: "#475569",
  marginBottom: "40px",
};

const section = {
  marginBottom: "40px",
};

const sectionTitle = {
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: "16px",
};

const paragraph = {
  fontSize: "1.05rem",
  lineHeight: "1.8",
  color: "#475569",
  marginBottom: "12px",
};

const list = {
  listStyleType: "disc",
  paddingLeft: "24px",
  marginTop: "12px",
};

const listItem = {
  fontSize: "1.05rem",
  lineHeight: "1.8",
  color: "#475569",
  marginBottom: "8px",
};

const contactInfo = {
  fontSize: "1.05rem",
  lineHeight: "1.8",
  color: "#475569",
  marginTop: "12px",
};

const link = {
  color: "#6366f1",
  textDecoration: "none",
  fontWeight: 600,
};

const footer = {
  background: "white",
  borderTop: "1px solid #e2e8f0",
  padding: "32px 24px",
  textAlign: "center",
  color: "#64748b",
  fontSize: "0.95rem",
};

export default TermsOfServicePage;
